import { api } from '../configs/apiConfig';

const ACCESS_CONTROL_ENDPOINT = '/accesscontrol';

class AccessControlService {
    // Get access types for a page
    async getPageAccessTypes(pageId) {
        try {
            const response = await api.get(`${ACCESS_CONTROL_ENDPOINT}/types/${pageId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching access types for page ${pageId}:`, error);
            throw error;
        }
    }

    // Add user access to a page
    async addUserAccess(accessData) {
        try {
            const response = await api.post(`${ACCESS_CONTROL_ENDPOINT}/user`, accessData);
            return response.data;
        } catch (error) {
            console.error('Error adding user access:', error);
            throw error;
        }
    }

    // Add organizational unit access to a page
    async addUnitAccess(accessData) {
        try {
            const response = await api.post(`${ACCESS_CONTROL_ENDPOINT}/unit`, accessData);
            return response.data;
        } catch (error) {
            console.error('Error adding unit access:', error);
            throw error;
        }
    }

    // Update access control
    async updateAccess(id, accessData) {
        try {
            const response = await api.put(`${ACCESS_CONTROL_ENDPOINT}/${id}`, accessData);
            return response.data;
        } catch (error) {
            console.error(`Error updating access control ${id}:`, error);
            throw error;
        }
    }

    // Remove access control
    async removeAccess(id) {
        try {
            await api.delete(`${ACCESS_CONTROL_ENDPOINT}/${id}`);
        } catch (error) {
            console.error(`Error removing access control ${id}:`, error);
            throw error;
        }
    }

    // Get flat list of accessible pages
    async getUserAccessiblePages(userId) {
        try {
            const response = await api.get(`${ACCESS_CONTROL_ENDPOINT}/pages/${userId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching accessible pages for user ${userId}:`, error);
            throw error;
        }
    }

    // Get hierarchical tree of accessible pages
    async getUserAccessiblePagesTree(userId) {
        try {
            const response = await api.get(`${ACCESS_CONTROL_ENDPOINT}/pages/tree/${userId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching accessible pages tree for user ${userId}:`, error);
            throw error;
        }
    }

    // Check if a user has permission to access a page
    async checkUserPermission(userId, pageId) {
        try {
            const response = await api.get(`${ACCESS_CONTROL_ENDPOINT}/checkPermission/${userId}/${pageId}`);
            return response.data;
        } catch (error) {
            console.error(`Error checking permission for user ${userId} on page ${pageId}:`, error);
            return false; // Return false on error as a safe default
        }
    }

    async checkPageAccess(userId, pageCode) {
        try {
            const response = await api.get(`${ACCESS_CONTROL_ENDPOINT}/checkPageAccess/${userId}/${pageCode}`);
            return response.data;
        } catch (error) {
            console.error(`Error checking page access for user ${userId} on page ${pageCode}:`, error);
            return false;
        }
    }

    // Get all access controls for a page
    async getPageAccessControls(pageId) {
        try {
            const response = await api.get(`${ACCESS_CONTROL_ENDPOINT}/page/${pageId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching access controls for page ${pageId}:`, error);
            throw error;
        }
    }

    // Get all access controls for an organizational unit
    async getUnitAccessControls(unitId) {
        try {
            const response = await api.get(`${ACCESS_CONTROL_ENDPOINT}/unit/${unitId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching access controls for unit ${unitId}:`, error);
            throw error;
        }
    }

    // Add user exclusion from a unit's access
    async addUserExclusion(exclusionData) {
        try {
            // We use the same endpoint but set isExcluded to true
            const data = { ...exclusionData, isExcluded: true };
            const response = await api.post(`${ACCESS_CONTROL_ENDPOINT}/user`, data);
            return response.data;
        } catch (error) {
            console.error('Error adding user exclusion:', error);
            throw error;
        }
    }

    // Check if a user is excluded from a specific unit's access to a page
    async checkUserExclusion(userId, unitId, pageId) {
        try {
            const response = await api.get(`${ACCESS_CONTROL_ENDPOINT}/exclusion/${userId}/${unitId}/${pageId}`);
            return response.data;
        } catch (error) {
            console.error(`Error checking exclusion for user ${userId} from unit ${unitId} on page ${pageId}:`, error);
            return false;
        }
    }

    // Get page access information (users and units with access)
    async getPageAccessInfo(pageId) {
        try {
            const response = await api.get(`${ACCESS_CONTROL_ENDPOINT}/page-access/${pageId}`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching access info for page ${pageId}:`, error);
            throw error;
        }
    }
}

export default new AccessControlService(); 